import { BadgeCheck, CircleAlert, DoorClosed, Loader2, Users } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { conferir, liberar } from "./api";

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const hora = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
};

// Tela da equipe ao ler o QR de saída (dono, 24/09/2026). Qualquer usuário
// logado libera. Liberar vale UMA vez: lido de novo, aparece "já saiu" com a
// hora e quem liberou — é o que derruba o print repassado. Com valor em
// aberto, dá pra liberar informando o motivo (os produtos viram da mesa).
// Quem pagou por outros pode liberar o grupo inteiro de uma vez.
export default function ConferirSaida({ codigo, onLido }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [acabou, setAcabou] = useState(null);

  const carregar = useCallback(async () => {
    const r = await conferir(codigo);
    if (r.ok) setDados(r.dados);
    else setErro(r.status === 401 ? "Entre no painel da loja neste aparelho para liberar a saída." : (r.erro?.mensagem || "QR não encontrado nesta loja."));
  }, [codigo]);

  useEffect(() => { carregar(); }, [carregar]);

  const dar = async (grupo) => {
    setEnviando(true);
    const r = await liberar(codigo, { grupo, motivo: motivo.trim() || null });
    setEnviando(false);
    if (!r.ok) {
      toast.error(r.erro?.mensagem || "Não consegui liberar.");
      return carregar();
    }
    setAcabou({ grupo, fechou: r.dados?.mesa_fechou, ficaram: r.dados?.ficaram || [] });
    setDados(r.dados);
    onLido?.();
  };

  if (erro) return <p className="text-center text-sm py-10 px-6" data-e2e="conferir-erro">{erro}</p>;
  if (!dados) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin opacity-60" /></div>;

  const saiu = dados.saida;
  const encerrada = !saiu && !dados.sessao_ativa;
  const deve = dados.em_aberto > 0;
  // Último da mesa: o que foi lançado para a mesa (sem dono) fica com ele
  const deveMesa = !deve && dados.ultimo_na_mesa && dados.mesa_em_aberto > 0;
  const precisaMotivo = deve || deveMesa;
  const grupoNaMesa = (dados.grupo || []).filter((g) => !g.saiu);
  // "Liberar o grupo" que esvazia a mesa leva junto o que é da mesa
  const grupoPrecisaMotivo = deve || (dados.grupo_esvazia_mesa && dados.mesa_em_aberto > 0);
  const mostrarMotivo = precisaMotivo || (grupoNaMesa.length > 0 && grupoPrecisaMotivo);
  const faixa = saiu && !acabou
    ? { cor: "bg-red-600", icone: DoorClosed, titulo: "JÁ SAIU", sub: `às ${hora(saiu.em)} · liberado por ${saiu.por || "equipe"}` }
    : saiu
      ? { cor: "bg-emerald-600", icone: BadgeCheck, titulo: "SAÍDA LIBERADA", sub: acabou?.fechou ? "Todos saíram — a mesa foi fechada" : `às ${hora(saiu.em)}` }
      : encerrada
        ? { cor: "bg-zinc-600", icone: DoorClosed, titulo: "CONTA ENCERRADA", sub: "Esta mesa já foi fechada no caixa — confira lá" }
        : deve
          ? { cor: "bg-amber-500", icone: CircleAlert, titulo: `EM ABERTO ${dinheiro(dados.em_aberto)}`, sub: "Ainda falta pagar" }
          : deveMesa
            ? { cor: "bg-amber-500", icone: CircleAlert, titulo: `MESA DEVE ${dinheiro(dados.mesa_em_aberto)}`, sub: "É o último da mesa: falta pagar o que foi lançado para a mesa" }
            : { cor: "bg-emerald-600", icone: BadgeCheck, titulo: "PAGO", sub: `${dinheiro(dados.pago_total)} pagos` };
  const Icone = faixa.icone;

  return (
    <div className="space-y-3" data-e2e="conferir-saida"
      data-estado={saiu ? (acabou ? "liberado" : "ja-saiu") : encerrada ? "encerrada" : precisaMotivo ? "aberto" : "pago"}>
      <div className={`${faixa.cor} text-white rounded-2xl px-4 py-4 text-center`}>
        <Icone className="w-9 h-9 mx-auto mb-1" />
        <p className="text-2xl font-black" data-e2e="conferir-titulo">{faixa.titulo}</p>
        <p className="text-sm opacity-90">{faixa.sub}</p>
      </div>
      <div className="rounded-2xl border bg-card px-4 py-3">
        <p className="font-black text-lg leading-tight">{dados.pessoa?.nome}</p>
        <p className="text-xs text-muted-foreground">
          {dados.mesa?.nome}{dados.pessoa?.cpf ? ` · CPF ${dados.pessoa.cpf}` : ""}{dados.pessoa?.origem === "painel" ? " · criado no painel" : ""}
        </p>
        {dados.pagamentos.length ? (
          <div className="mt-2 space-y-1">
            {dados.pagamentos.map((p) => (
              <p key={p.id} className="text-sm flex justify-between">
                <span>{p.paguei ? "Pagou" : `Pago por ${p.por}`} · {hora(p.hora)}</span>
                <span className="font-bold tabular-nums">{dinheiro(p.total)}</span>
              </p>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground mt-2">Nenhum pagamento pelo celular.</p>}
        {saiu?.motivo ? <p className="text-xs text-amber-600 mt-2">Liberado com valor em aberto: {saiu.motivo}</p> : null}
      </div>
      {acabou?.ficaram?.length ? (
        <p className="text-xs text-amber-600">Ficaram na mesa (têm valor em aberto — libere cada um pelo QR dele): {acabou.ficaram.join(", ")}</p>
      ) : null}
      {!saiu && !encerrada ? (
        <div className="space-y-2">
          {mostrarMotivo ? (
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo para liberar com valor em aberto"
              data-e2e="conferir-motivo" className="w-full h-11 rounded-xl border px-3 bg-background" />
          ) : null}
          <button type="button" onClick={() => dar(false)} disabled={enviando || (precisaMotivo && !motivo.trim())} data-e2e="conferir-liberar"
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground text-lg font-black disabled:opacity-50">
            {enviando ? "Liberando..." : "LIBERAR SAÍDA"}
          </button>
          {grupoNaMesa.length ? (
            <button type="button" onClick={() => dar(true)} disabled={enviando || (grupoPrecisaMotivo && !motivo.trim())} data-e2e="conferir-grupo"
              title="Quem do grupo ainda deve fica na mesa"
              className="w-full h-12 rounded-2xl border-2 border-primary/40 text-primary font-black flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Users className="w-4 h-4" /> LIBERAR O GRUPO ({grupoNaMesa.length + 1} pessoas)
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
